import { ListItemProps } from "@progress/kendo-react-dropdowns";
import { Tooltip } from "@progress/kendo-react-tooltip";
import isFunction from "lodash-es/isFunction";
import * as React from "react";
import { If, Then } from "react-if";
import TitleFilter from "@/helpers/title_filter";
import DialogIcon from "./dialog.svg?react";

const FormSelectItemRender = (
  // Kendo types its `itemRender` element as `ReactElement<HTMLLIElement>`, but
  // `ReactElement<P>` takes the element's PROPS type, not the element type. The
  // parameter has to keep Kendo's shape to stay assignable at the call sites, so
  // the mistyping is corrected once below via `item`.
  li: React.ReactElement<HTMLLIElement>,
  itemProps: ListItemProps & {
    descriptionField?: string;
    imageRender?: (dataItem: ListItemProps["dataItem"]) => React.ReactNode;
  },
) => {
  const dataItem = itemProps.dataItem;
  const title = dataItem?.[itemProps.textField || "name"];
  const description = itemProps.descriptionField
    ? dataItem?.[itemProps.descriptionField]
    : undefined;
  const optionInfoText = dataItem?.optionInfoText;
  const hasDialog = dataItem.openDialog;

  // The single cast that undoes Kendo's mistyping (see the parameter comment).
  // It has to be on the element, not just on `li.props`, because `cloneElement`
  // infers its accepted props from the element's type too. Everything set on
  // `props` below is then checked against the real prop bag — which is exactly
  // what an `as typeof props` on the assignment would have silenced instead.
  const item = li as unknown as React.ReactElement<
    React.LiHTMLAttributes<HTMLLIElement>
  >;
  let props: React.LiHTMLAttributes<HTMLLIElement> = { ...item.props };

  if (dataItem.disabled || dataItem.checkAllDisabled) {
    props = {
      ...props,
      className: "k-list-item k-disabled",
      "aria-disabled": true,
      // Kendo's `skipDisabledItems` only guards keyboard navigation — the list
      // item's own handlers still select it on click, so swallow them here.
      // These deliberately REPLACE Kendo's own handlers rather than composing
      // with them: composing would still run the handler that selects the item,
      // which is the behaviour being suppressed.
      onClick: (event: React.MouseEvent) => event.stopPropagation(),
      onMouseDown: (event: React.MouseEvent) => event.preventDefault(),
    };
  }

  if (title || optionInfoText || hasDialog) {
    props = {
      ...props,
      style: {
        ...props.style,
        display: "block",
      },
    };

    return (
      <Tooltip
        anchorElement="target"
        position="top"
        filter={TitleFilter}
        style={{ maxWidth: "18.75rem", wordBreak: "break-word" }}
      >
        {React.cloneElement(
          item,
          props,
          <span className="k-d-flex k-d-flex-row k-d-flex-space-between k-d-flex-align-center">
            <If condition={isFunction(itemProps.imageRender)}>
              <Then>{itemProps.imageRender?.(dataItem)}</Then>
            </If>
            <span
              className="longTitleOnly kendo-custom-tooltip"
              title={title}
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                fontWeight: description ? 700 : "normal",
                flex: 1,
              }}
            >
              {title}
            </span>
            <If condition={hasDialog}>
              <Then>
                <DialogIcon />
              </Then>
            </If>
            <If condition={optionInfoText}>
              <Then>
                <span
                  title={optionInfoText}
                  className="k-icon k-font-icon k-i-info info-icon kendo-custom-tooltip"
                  style={{
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: "auto",
                  }}
                />
              </Then>
            </If>
            <If condition={!!description}>
              <Then>
                <span className="description">{description}</span>
              </Then>
            </If>
          </span>,
        )}
      </Tooltip>
    );
  } else {
    return React.cloneElement(item, props, <>{title}</>);
  }
};

export default FormSelectItemRender;
